import type { Locales } from "@/i18n/i18n-types"

export const formatLikeMMdotDDdotYYYY = function (d: Date | null | undefined, includeTime: boolean = false): string {
  if (!d) return ''
  const date = new Date(d)
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yyyy = date.getFullYear()

  if(includeTime) {
    let hours = date.getHours()
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const ampm = hours >= 12 ? 'pm' : 'am'
    hours = hours % 12 || 12
    const hh = String(hours).padStart(2, '0')
    return `${mm}.${dd}.${yyyy} ${hh}:${minutes}${ampm}`
  }

  return `${mm}.${dd}.${yyyy}`
}

const relativeTimeFormat = new Map<string, Intl.RelativeTimeFormat>()
const absoluteTimeFormat = new Map<string, Intl.DateTimeFormat>()

function getRelativeFormatter(locale: string) {
  if (!relativeTimeFormat.has(locale)) {
    relativeTimeFormat.set(locale, new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }))
  }

  return relativeTimeFormat.get(locale)!
}

function getAbsoluteFormatter(locale: string) {
  if (!absoluteTimeFormat.has(locale)) {
    absoluteTimeFormat.set(locale, new Intl.DateTimeFormat([locale], { dateStyle: 'medium' }))
  }

  return absoluteTimeFormat.get(locale)!
}

function getRelativeTimeFormat(val: number, unit: Intl.RelativeTimeFormatUnit, locale: string) {
  const formatter = getRelativeFormatter(locale)
  return formatter.format(val, unit)
}

function getAbsoluteTimeFormat(date: Date, locale: string) {
  const formatter = getAbsoluteFormatter(locale)
  return formatter.format(date)
}

const cutoffs = [ 60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity ];
const units: Intl.RelativeTimeFormatUnit[] = [ "second", "minute", "hour", "day", "week", "month", "year" ];

export function getTimeFormat(date: Date, locale: Locales) {
  const now = Date.now();

  if (now < date.getTime()) return 'Just Now'

  const twoDaysAgo = now - (86400 * 1000 * 2)

  if (date.getTime() < twoDaysAgo)
    return getAbsoluteTimeFormat(date, locale)

  const diffInSeconds = -Math.floor((now - date.getTime()) / 1000);

  const unitIndex = cutoffs.findIndex(cutoff => cutoff > Math.abs(diffInSeconds));
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;
  const unit = units[unitIndex];
  const val = Math.floor(diffInSeconds / divisor);

  return getRelativeTimeFormat(val, unit, locale)
}
